import { useCallback, useEffect, useState } from "react";

export const THEMES = ["light", "dark", "system"] as const;
export type Theme = (typeof THEMES)[number];

const STORAGE_KEY = "theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

function isTheme(value: unknown): value is Theme {
  return typeof value === "string" && (THEMES as readonly string[]).includes(value);
}

/**
 * Storage throws outright in some contexts (private mode, blocked site data),
 * so every access is guarded and falls back to the "system" default.
 */
function readStored(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isTheme(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

function writeStored(theme: Theme) {
  try {
    if (theme === "system") {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, theme);
    }
  } catch {
    /* preference just won't persist */
  }
}

function prefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia(DARK_QUERY).matches;
}

/** Resolves "system" down to the concrete theme actually being displayed. */
export function resolveTheme(theme: Theme): "light" | "dark" {
  if (theme === "system") return prefersDark() ? "dark" : "light";
  return theme;
}

function applyTheme(theme: Theme) {
  const resolved = resolveTheme(theme);
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readStored);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    writeStored(next);
    applyTheme(next);
  }, []);

  /** Cycles light → dark → system → light. */
  const cycleTheme = useCallback(() => {
    setTheme(THEMES[(THEMES.indexOf(readStored()) + 1) % THEMES.length]);
  }, [setTheme]);

  // Keep the DOM in sync on mount: the inline <head> script already applied the
  // right class, but this covers a hydration mismatch or a stale class.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // While on "system", follow the OS live rather than only at load.
  useEffect(() => {
    if (theme !== "system") return;
    const media = window.matchMedia(DARK_QUERY);
    const onChange = () => applyTheme("system");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [theme]);

  return { theme, setTheme, cycleTheme, resolved: resolveTheme(theme) };
}
